import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { useAppStore } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { EditorColorSchemeId, EditorColorSelection } from '../../../../core/theme/monacoColorSchemes';
import { JAM_MONACO_DARK, JAM_MONACO_LIGHT } from '../../../../core/theme/monacoTheme';
import {
  diffCustomColorPickerChanges,
  resolveEditorColorTables,
  seedCustomEditorColors,
  type CustomEditorColors,
} from '../../../../core/theme/customEditorColors';
import { SchemePreview } from './SchemePreview';
import { EditorColorSchemeSelect } from './EditorColorSchemeSelect';
import { CustomEditorColorsSection } from './CustomEditorColorsSection';

interface EditorColorSchemeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Settings → UI → Editor colors dialog — STAGED editing: opening the dialog
 * snapshots the committed store state (`editorColorScheme` +
 * `customEditorColors`) into local draft state. Every control inside the
 * dialog (the scheme list, the Custom picker rows, "Copy from…") edits the
 * draft only — nothing reaches the store or Monaco until "Confirm". Cancel,
 * the X button, Escape, and an outside click all discard the draft by
 * closing without committing; reopening re-snapshots from whatever is
 * committed at that point.
 *
 * Nested inside the Settings dialog — Radix's dismissable-layer stack
 * handles the nesting (Escape closes this one first;
 * `useClearStuckBodyPointerEvents`, already wired into `DialogContent`,
 * guards the "stuck body pointer-events" failure mode a naive nested-modal
 * setup would otherwise hit).
 *
 * Two preview panes at the top render `previewId ?? the draft selection` —
 * hovering an open scheme option previews it live WITHOUT touching the
 * draft; only picking one updates the draft selection.
 */
export function EditorColorSchemeDialog({ open, onOpenChange }: EditorColorSchemeDialogProps) {
  const editorColorScheme = useAppStore((state) => state.editorColorScheme);
  const customEditorColors = useAppStore((state) => state.customEditorColors);
  const isDarkMode = useAppStore((state) => state.isDarkMode);
  const applyEditorColors = useAppStore((state) => state.applyEditorColors);
  const { track } = useAnalytics();

  const [draftSelection, setDraftSelection] = useState<EditorColorSelection>(editorColorScheme);
  const [draftCustomColors, setDraftCustomColors] = useState<CustomEditorColors | null>(customEditorColors);
  const [previewId, setPreviewId] = useState<EditorColorSelection | undefined>(undefined);
  // Lifted so Escape can be intercepted here — see `EditorColorSchemeSelect`'s
  // `open`/`onOpenChange` doc comment for why an uncontrolled Select nested
  // in this Dialog would close BOTH on a single Escape press.
  const [schemeSelectOpen, setSchemeSelectOpen] = useState(false);

  // What Confirm diffs against for analytics — the committed state at the
  // moment the dialog opened (not necessarily today's props once the draft
  // has diverged from them).
  const snapshotRef = useRef({ selection: editorColorScheme, customColors: customEditorColors });

  // Re-snapshot every time the dialog opens; deliberately keyed only on
  // `open` — the committed store state must NOT re-clobber the draft while
  // the dialog stays open (see `MigrateTableSubsetDialog` for the same
  // open-only-seed pattern).
  useEffect(() => {
    if (!open) return;
    snapshotRef.current = { selection: editorColorScheme, customColors: customEditorColors };
    setDraftSelection(editorColorScheme);
    setDraftCustomColors(customEditorColors);
    setPreviewId(undefined);
    setSchemeSelectOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const tables = resolveEditorColorTables(previewId ?? draftSelection, draftCustomColors);

  function selectDraftScheme(next: EditorColorSelection) {
    if (next === draftSelection) return;
    if (next === 'custom' && draftCustomColors === null) {
      // First time the draft reaches Custom: start from whatever preset the
      // draft was on (falling back to 'default' if it was somehow already
      // 'custom' with no table — defensive only). Purely local — the store
      // is untouched until Confirm.
      const seedFrom: EditorColorSchemeId = draftSelection === 'custom' ? 'default' : draftSelection;
      setDraftCustomColors(seedCustomEditorColors(seedFrom));
    }
    setDraftSelection(next);
  }

  function handleConfirm() {
    const snapshot = snapshotRef.current;

    if (draftSelection !== snapshot.selection) {
      track('feature:editor_color_scheme_changed', { scheme: draftSelection });
    }
    for (const change of diffCustomColorPickerChanges(draftCustomColors, snapshot.customColors)) {
      track('feature:editor_custom_color_set', { ...change });
    }

    applyEditorColors(draftSelection, draftCustomColors);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[680px]"
        data-testid="editor-color-scheme-dialog"
        onEscapeKeyDown={(event) => {
          // The Scheme dropdown is open: keep THIS dialog open and close
          // just the dropdown instead (see `EditorColorSchemeSelect`'s
          // `open`/`onOpenChange` doc comment for why Escape would otherwise
          // close both at once).
          if (schemeSelectOpen) {
            event.preventDefault();
            setSchemeSelectOpen(false);
            // This bypasses `EditorColorSchemeSelect`'s own `onOpenChange`
            // composition (which normally clears the preview too) — Escape
            // never reaches it because the popover's own dismiss-on-escape
            // gets suppressed by the shared `event.defaultPrevented` flag.
            setPreviewId(undefined);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Editor colors</DialogTitle>
          <DialogDescription>Syntax highlighting colors for SQL editors.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 min-[560px]:grid-cols-2">
          <div className="space-y-1">
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Dark</div>
            <SchemePreview
              colors={tables.dark}
              background={JAM_MONACO_DARK.colors['editor.background']}
              data-testid="scheme-preview-dark"
            />
          </div>
          <div className="space-y-1">
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Light</div>
            <SchemePreview
              colors={tables.light}
              background={JAM_MONACO_LIGHT.colors['editor.background']}
              data-testid="scheme-preview-light"
            />
          </div>
        </div>

        <EditorColorSchemeSelect
          selection={draftSelection}
          isDarkMode={isDarkMode}
          customEditorColors={draftCustomColors}
          onHoverPreview={setPreviewId}
          onSelect={selectDraftScheme}
          open={schemeSelectOpen}
          onOpenChange={setSchemeSelectOpen}
        />

        {draftSelection === 'custom' && draftCustomColors && (
          <CustomEditorColorsSection
            colors={draftCustomColors}
            onColorChange={(variant, tokens, hex) =>
              setDraftCustomColors((current) => {
                const base = current ?? seedCustomEditorColors('default');
                const nextVariant = { ...base[variant] };
                for (const token of tokens) nextVariant[token] = hex;
                return { ...base, [variant]: nextVariant };
              })
            }
            onCopyFrom={(scheme) => setDraftCustomColors(seedCustomEditorColors(scheme))}
          />
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="editor-colors-cancel"
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} data-testid="editor-colors-confirm">
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
